import RBush from 'rbush';
import {Axis} from '../axis/axis';
import {RBushItem} from "../crosspoint/types/r-bush-item.interface";
import {PositionedItem} from "../crosspoint/types/positioned-item.interface";
import {ViewportItem} from "../crosspoint/types/viewport-item.interface";
import {Viewport} from "../crosspoint/types/viewport.interface";


/**
 * Main class for spatial matrix with Z-stacking
 *
 * **BREAKING CHANGE (v2.0):** PositionedItem now stores numeric indices!
 *
 * @template TXValue - TXAxisValue-axis value type (e.g., DateTime)
 * @template TYValue - TYAxisValue-axis value type (e.g., string UUID)
 * @template TItem - Domain item type (e.g., WorkItem)
 */
export class SpatialMatrix<TXValue, TYValue, TItem> {

    private tree = new RBush<RBushItem<TItem>>();

    private items = new Map<string, PositionedItem<TItem>>();

    private insertCounter = 0;

    private viewportCache = new Map<string, ViewportItem<TItem>[]>();

    /**
     * Creates an instance of the class.
     *
     * @param {Axis<TXValue, any>} xAxis The axis representing the x-dimension.
     * @param {Axis<TYValue, any>} yAxis The axis representing the y-dimension.
     */
    constructor(
        private readonly xAxis: Axis<TXValue, any>,
        private readonly yAxis: Axis<TYValue, any>
    ) {
    }

    /**
     * Add an item to the matrix
     *
     * @param id - Unique item ID
     * @param valueRangeX - TXAxisValue-axis range (raw values, e.g., [DateTime, DateTime])
     * @param valueRangeY - TYAxisValue-axis range (raw values, e.g., ['alice', 'alice'])
     * @param data - Domain item data
     *
     * @example
     * // Input: Raw values
     * matrix.addItem(
     *   'task-1',
     *   [DateTime('2025-01-01'), DateTime('2025-01-31')],
     *   ['alice', 'alice'],
     *   workItem
     * );
     */
    addItem(
        id: string,
        valueRangeX: [TXValue, TXValue],
        valueRangeY: [TYValue, TYValue],
        data: TItem
    ): void {
        const [startValueX, endValueX] = valueRangeX;
        const [startValueY, endValueY] = valueRangeY;

        // Convert raw values to numeric indices
        const startIndexX = this.xAxis.getIndex(startValueX);
        const endIndexX = this.xAxis.getIndex(endValueX);
        const startIndexY = this.yAxis.getIndex(startValueY);
        const endIndexY = this.yAxis.getIndex(endValueY);

        if (startIndexX === undefined || endIndexX === undefined ||
            startIndexY === undefined || endIndexY === undefined) {
            throw new Error(`Cannot add item: Values not found in axis cache. Ensure ensureRange() was called before adding items.`);
        }

        // Check if item already exists (idempotence)
        const existingItem = this.items.get(id);
        if (existingItem) {
            this.removeItem(id);
        }

        const item: PositionedItem<TItem> = {
            id,
            data,
            indexRangeX: [startIndexX, endIndexX],
            indexRangeY: [startIndexY, endIndexY],
            insertOrder: this.insertCounter++
        };

        // Store item (overwrites existing)
        this.items.set(id, item);

        // Add to RBush (already numeric coordinates)
        const rbushItem: RBushItem<TItem> = {
            minX: Math.min(startIndexX, endIndexX),
            maxX: Math.max(startIndexX, endIndexX),
            minY: Math.min(startIndexY, endIndexY),
            maxY: Math.max(startIndexY, endIndexY),
            data: item
        };

        this.tree.insert(rbushItem);

        // Invalidate relevant caches
        this.invalidateOverlappingCaches(rbushItem);
    }

    /**
     * Removes an item identified by its unique ID from the collection and updates related indices and caches.
     *
     * @param {string} id - The unique identifier of the item to remove.
     * @return {boolean} Returns true if the item was found and successfully removed, otherwise false.
     */
    removeItem(id: string): boolean {
        const item = this.items.get(id);

        if (!item) {
            return false;
        }

        const [existingItemIndexX0, existingItemIndexX1] = item.indexRangeX;
        const [existingItemIndexY0, existingItemIndexY1] = item.indexRangeY;

        // Remove old RBush entry
        const rbushItem = this.tree.search({
            minX: Math.min(existingItemIndexX0, existingItemIndexX1),
            maxX: Math.max(existingItemIndexX0, existingItemIndexX1),
            minY: Math.min(existingItemIndexY0, existingItemIndexY1),
            maxY: Math.max(existingItemIndexY0, existingItemIndexY1)
        }).find(rbi => rbi.data.id === id);

        if (rbushItem) {
            this.tree.remove(rbushItem);
            this.invalidateOverlappingCaches(rbushItem);
        }

        this.items.delete(id);

        return true;
    }

    /**
     * Queries and retrieves items within the specified viewport boundaries.
     * The method leverages caching for improved performance and uses spatial indexing
     * to efficiently search within the viewport.
     *
     * @param {Viewport} viewport - The viewport object representing the boundaries to query.
     *                              It contains numeric index ranges for both X and Y axes.
     * @return {ViewportItem<TItem>[]} An array of items within the specified viewport,
     *                                 sorted by their insertion order and processed
     *                                 with calculated Z-indices.
     */
    queryViewport(viewport: Viewport): ViewportItem<TItem>[] {
        // Generate cache key
        const cacheKey = this.getViewportCacheKey(viewport);

        // Check cache
        if (this.viewportCache.has(cacheKey)) {
            return this.viewportCache.get(cacheKey)!;
        }

        // Viewport already has numeric indices - use directly!
        const [minX, maxX] = viewport.indexRangeX;
        const [minY, maxY] = viewport.indexRangeY;

        // Search items with RBush
        const found = this.tree.search({minX, maxX, minY, maxY});

        // Extract items and sort by insert order
        const items = found
            .map(rbi => rbi.data)
            .sort((a, b) => a.insertOrder - b.insertOrder);

        // Calculate Z-indices (Lazy Z-Calculation)
        const result = this.calculateZIndices(items, viewport);

        // Cache result
        this.viewportCache.set(cacheKey, result);

        return result;
    }

    /**
     * Calculate Z-indices for items in viewport
     *
     * **V2.0:** Works with ViewportWindow (numeric indices)
     */
    private calculateZIndices(
        items: PositionedItem<TItem>[],
        viewport: Viewport
    ): ViewportItem<TItem>[] {
        const result: ViewportItem<TItem>[] = [];

        for (const item of items) {
            // Find overlapping items that were already processed
            const overlapping = result.filter(processed =>
                this.itemsOverlap(item, processed)
            );

            // Calculate dense Z-index
            const z = overlapping.length === 0
                ? 0
                : Math.max(...overlapping.map(i => i.z)) + 1;

            // Create ViewportItem with z-index and viewport context
            const viewportItem = this.toViewportItem(item, viewport, z);

            result.push(viewportItem);
        }

        return result;
    }

    /**
     * Determines whether two items overlap based on their positional ranges.
     *
     * @param {PositionedItem<TItem>} a - The first item with positional ranges.
     * @param {PositionedItem<TItem>} b - The second item with positional ranges.
     * @return {boolean} Returns true if the items overlap; otherwise, false.
     */
    private itemsOverlap(
        a: PositionedItem<TItem>,
        b: PositionedItem<TItem>
    ): boolean {

        const [aXStart, aXEnd] = a.indexRangeX;
        const [aYStart, aYEnd] = a.indexRangeY;
        const [bXStart, bXEnd] = b.indexRangeX;
        const [bYStart, bYEnd] = b.indexRangeY;

        // Check for non-overlap
        return !(
            aXEnd < bXStart ||   // a completely left of b
            aXStart > bXEnd ||   // a completely right of b
            aYEnd < bYStart ||   // a completely above b
            aYStart > bYEnd      // a completely below b
        );
    }

    /**
     * Convert PositionedItem to ViewportItem with viewport context
     *
     * Creates a complete ViewportItem with:
     * - Z-index (passed as parameter)
     * - Visible ranges in raw values (ALWAYS set!)
     * - Clipping flags
     *
     * **V2.0 Algorithm:**
     * 1. Item already has numeric indices (spanX/indexRangeY)
     * 2. Viewport already has numeric indices (ViewportWindow)
     * 3. Compare indices to determine clipping
     * 4. Convert visible indices to raw values (ALWAYS!)
     *
     * @param item - PositionedItem with numeric indices
     * @param viewport - ViewportWindow with numeric indices
     * @param z - Calculated Z-index for this item
     * @returns Complete ViewportItem with all fields set
     * @throws Error if indices cannot be converted to raw values
     */
    private toViewportItem(
        item: PositionedItem<TItem>,
        viewport: Viewport,
        z: number
    ): ViewportItem<TItem> {
        // Item already has numeric indices (v2.0)
        const [itemXStart, itemXEnd] = item.indexRangeX;
        const [itemYStart, itemYEnd] = item.indexRangeY;

        // Viewport already has numeric indices - use directly!
        const [viewXStart, viewXEnd] = viewport.indexRangeX;
        const [viewYStart, viewYEnd] = viewport.indexRangeY;

        // Clipping flags
        const isClippedLeft = itemXStart < viewXStart;
        const isClippedRight = itemXEnd > viewXEnd;
        const isClippedTop = itemYStart < viewYStart;
        const isClippedBottom = itemYEnd > viewYEnd;

        // Calculate visible ranges (ALWAYS - for rendering!)
        const visibleXStart = Math.max(itemXStart, viewXStart);
        const visibleXEnd = Math.min(itemXEnd, viewXEnd);
        const visibleYStart = Math.max(itemYStart, viewYStart);
        const visibleYEnd = Math.min(itemYEnd, viewYEnd);

        return {
            ...item, // id, data, spanX, indexRangeY, insertOrder
            z,
            visibleIndexRangeX: [visibleXStart, visibleXEnd],
            visibleIndexRangeY: [visibleYStart, visibleYEnd],
            isClippedLeft,
            isClippedRight,
            isClippedTop,
            isClippedBottom
        };
    }

    /**
     * Invalidate cache entries that overlap an item.
     *
     * Only removes cache entries for viewports that actually overlap with the
     * item's bounding box. This is more efficient than clearing the entire cache.
     *
     * @param item - The RBush item with bounding box coordinates
     */
    private invalidateOverlappingCaches(item: RBushItem<TItem>): void {
        for (const cacheKey of this.viewportCache.keys()) {
            const [minX, maxX, minY, maxY] = cacheKey.split(',').map(Number);

            // AABB overlap test: check if item overlaps viewport
            const overlaps = !(
                item.maxX < minX ||  // Item completely left of viewport
                item.minX > maxX ||  // Item completely right of viewport
                item.maxY < minY ||  // Item completely above viewport
                item.minY > maxY     // Item completely below viewport
            );

            if (overlaps) {
                this.viewportCache.delete(cacheKey);
            }
        }
    }

    /**
     * Generate a cache key for a viewport
     */
    private getViewportCacheKey(viewport: Viewport): string {
        const [xMin, xMax] = viewport.indexRangeX;
        const [yMin, yMax] = viewport.indexRangeY;

        return `${xMin},${xMax},${yMin},${yMax}`;
    }

    /**
     * Get all items (without Z-calculation)
     */
    getAllItems(): PositionedItem<TItem>[] {
        return Array.from(this.items.values());
    }

    /**
     * Get a specific item
     */
    getItem(id: string): PositionedItem<TItem> | undefined {
        return this.items.get(id);
    }

    /**
     * Removes all items whose ID matches the given prefix.
     *
     * Matches items where ID equals prefix exactly, or starts with prefix + "_".
     * Items are collected first, then removed via removeItem() to ensure
     * consistent RBush and cache invalidation.
     *
     * @param prefix - The prefix to match against item IDs
     * @returns Array of removed PositionedItems (for region invalidation by caller)
     */
    removeItemsByPrefix(prefix: string): PositionedItem<TItem>[] {
        const matchingIds: string[] = [];

        for (const id of this.items.keys()) {
            if (id === prefix || id.startsWith(prefix + '_')) {
                matchingIds.push(id);
            }
        }

        const removed: PositionedItem<TItem>[] = [];

        for (const id of matchingIds) {
            const item = this.items.get(id);
            if (item) {
                removed.push(item);
                this.removeItem(id);
            }
        }

        return removed;
    }

    /**
     * Clear all items
     */
    clear(): void {
        this.tree.clear();
        this.items.clear();
        this.viewportCache.clear();
        this.insertCounter = 0;
    }

    /**
     * Get the number of items
     */
    size(): number {
        return this.items.size;
    }

    /**
     * Get statistics about the matrix
     */
    getStats(): {
        itemCount: number;
        xAxisSize: number;
        yAxisSize: number;
        cacheSize: number;
    } {
        return {
            itemCount: this.items.size,
            xAxisSize: this.xAxis.size(),
            yAxisSize: this.yAxis.size(),
            cacheSize: this.viewportCache.size
        };
    }

    /**
     * Get TXAxisValue-axis instance
     * Used by Timeline to access axis for index conversion
     */
    getXAxis(): Axis<TXValue> {
        return this.xAxis;
    }

    /**
     * Get TYAxisValue-axis instance
     * Used by Timeline to access axis for index conversion
     */
    getYAxis(): Axis<TYValue> {
        return this.yAxis;
    }
}